/**
 * GH #336: stop a context menu from self-activating its first item.
 *
 * jQuery-contextMenu activates a menu item on MOUSEUP, delegated on document, and
 * inspects neither the button nor the menu's age. WebKit dispatches `contextmenu`
 * on the PRESS and the library builds and positions the menu synchronously inside
 * that handler, with the menu's top-left placed exactly on the click point. So by
 * the time the user releases the button that opened the menu, the first item is
 * already painted under the pointer and that release runs it. In BuilderPlan that
 * is "Rename Project", which is why a Safari right-click on a project tab jumps
 * straight into the Edit project dialog.
 *
 * The library's own guard cannot help: isInteractionTooFast returns false
 * immediately on any non-touch device, AND returns false for root-level items
 * regardless of device. "Rename Project" is a root-level item on a Mac.
 *
 * THE RULE: a menu item may only be activated by a release belonging to a NEW pointer
 * gesture, never by the release of the gesture that opened the menu.
 *
 * WHY NOT the obvious alternatives (all were tried and rejected, do not "simplify"
 * this back into one of them):
 *
 *  - Testing the mouse button (`if (ev.button === 0) return`) looks equivalent and is
 *    not. macOS Ctrl+click is a first-class secondary click, WebKit dispatches
 *    `contextmenu` for it, and BOTH its press and its release carry button === 0, so a
 *    button test misses it entirely and the bug survives on trackpads. This codebase
 *    already knows Mac button values are untrustworthy here: util/keyboard.ts force-maps
 *    contextmenu to button 2 on Mac. Keying on gesture structure needs no button value
 *    and so covers right-click, Ctrl+click, two-finger tap and long-press alike.
 *
 *  - The library's `itemClickEvent: 'click'` option is read ONCE per page, behind a
 *    module-global `initialized` flag, from whichever $.contextMenu create call
 *    happens to run first. It would have to be set at all three create sites and
 *    would silently evaporate the day someone adds a fourth.
 *
 * WHY TIMESTAMPS and not a simple "any press inside the menu disarms": that would
 * depend on the browser delivering `mousedown` BEFORE `contextmenu`. If an engine fires
 * `contextmenu` first, the opening gesture's own press lands on the freshly painted menu
 * and would disarm the guard, resurrecting the bug. A press belonging to the opening
 * gesture arrives within a few milliseconds of the `contextmenu`; a deliberate press by
 * a user who has looked at the menu is hundreds of milliseconds later. Comparing
 * timestamps therefore works under EITHER event order.
 *
 * Keyboard activation is untouched: the library activates the focused item with jQuery's
 * synthetic $selected.trigger('mouseup'). jQuery's trigger() only dispatches a native
 * DOM event when elem[type] is a function, and HTMLLIElement has no .mouseup() method,
 * so no native event is ever produced and a native listener cannot see it.
 */

/**
 * A press this close to the `contextmenu` event (in ms) is part of the same physical
 * gesture rather than a deliberate new one. Real pointer gestures deliver both within
 * a frame or two; a human reading the menu before clicking takes far longer.
 */
export const SAME_GESTURE_MS = 50;

/** Disarmed sentinel. Kept out of `null` so the field stays a number. */
const DISARMED = -1;

/**
 * The pure decision half of the guard, free of any DOM dependency so it can be
 * specced under the node test runner.
 */
export class ContextMenuGestureGuard {
    private armedAt: number = DISARMED;

    /** A context menu is opening. Its trailing release must not activate anything. */
    onContextMenu(timeStamp: number): void {
        this.armedAt = timeStamp;
    }

    /**
     * A press. If it is a genuinely new gesture it means the user is deliberately
     * clicking, so the guard stands down. A press that is part of the opening gesture
     * (i.e. effectively simultaneous with the contextmenu) must NOT disarm us, or an
     * engine that orders contextmenu before mousedown would defeat the guard.
     */
    onMouseDown(timeStamp: number): void {
        if (this.armedAt !== DISARMED && timeStamp - this.armedAt > SAME_GESTURE_MS) {
            this.armedAt = DISARMED;
        }
    }

    /**
     * A release. Consumes the armed state either way: exactly one release belongs to
     * the opening gesture, so the guard must never persist past it and swallow a real
     * click later on.
     *
     * @returns true when this release must be swallowed rather than reaching the menu.
     */
    onMouseUp(insideMenu: boolean): boolean {
        const wasArmed = this.armedAt !== DISARMED;
        this.armedAt = DISARMED;
        return wasArmed && insideMenu;
    }

    /** Exposed for specs. */
    get isArmed(): boolean {
        return this.armedAt !== DISARMED;
    }
}

/** Every jQuery-contextMenu menu and submenu <ul> carries this class. */
export const MENU_SELECTOR = '.context-menu-list';

/**
 * Wire the guard to a document. Listeners are registered in the CAPTURE phase so a
 * swallowed release is stopped before it can reach the library's document-delegated
 * bubble-phase handler.
 */
export function installContextMenuGestureGuard(doc: Document): ContextMenuGestureGuard {
    const guard = new ContextMenuGestureGuard();

    doc.addEventListener('contextmenu', (ev: Event) => {
        guard.onContextMenu(ev.timeStamp);
    }, true);

    doc.addEventListener('mousedown', (ev: Event) => {
        guard.onMouseDown(ev.timeStamp);
    }, true);

    doc.addEventListener('mouseup', (ev: Event) => {
        const target = ev.target;
        const insideMenu = target instanceof Element && !!target.closest(MENU_SELECTOR);
        if (guard.onMouseUp(insideMenu)) {
            // Stop here, in capture, so the library's delegated mouseup handler
            // never sees this release.
            ev.stopPropagation();
        }
    }, true);

    return guard;
}
